// Category-scrape sub-form for the "New Collection" drawer. Owns its own redux
// data lifecycle (Amazon domain list + the user's active zipcodes for the chosen
// domain) so it isn't re-run on unrelated CollectionForm keystrokes.
//
// Contract note: the category API wants the domain STRING (e.g. amazon.com.au)
// and the postcode STRING (e.g. 2000) — NOT the numeric ids the ASIN flow emits.
// We keep both: the id drives the zipcode fetch, the strings go in the payload.
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  postCodeDomainList,
  setZipCodeList,
} from "../../../reducer/zipCode/ZipCode.action";
import { deliveryDayOptions, REFINEMENT_DOMAINS } from "../../../data/constants";

export const dollarsToCents = (v) =>
  v === "" || v == null ? null : Math.round(parseFloat(v) * 100);

// Best-effort node id from an Amazon category URL. Manual override always wins.
export const extractNodeId = (url) => {
  if (!url) return "";
  const m =
    url.match(/[?&]node=(\d+)/) ||
    url.match(/\bnode[=/](\d+)/) ||
    url.match(/n(?:%3A|:)(\d+)/i) ||
    url.match(/\/(\d{4,})(?:[/?#]|$)/);
  return m ? m[1] : "";
};

export const CategoryFields = ({ value, onChange }) => {
  const dispatch = useDispatch();
  const domainList = useSelector((s) => s.zipCodeReducer.domainList);
  const zipCodesList = useSelector((s) => s.zipCodeReducer.zipCodesList);

  const amazonDomains = useMemo(
    () => (domainList || []).filter((d) => d.domain?.startsWith("amazon.")),
    [domainList]
  );

  // Load the domain list once.
  useEffect(() => {
    dispatch(postCodeDomainList({ status: "active" }));
  }, [dispatch]);

  // Default to the first Amazon domain once the list arrives.
  useEffect(() => {
    if (!value.domainId && amazonDomains.length > 0) {
      const d = amazonDomains[0];
      onChange({ domainId: String(d.id), domain: d.domain, postcode: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amazonDomains]);

  // Load active zipcodes (cookies present) for the chosen domain.
  useEffect(() => {
    if (value.domainId) {
      dispatch(setZipCodeList({ status: "active", domain: value.domainId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.domainId, dispatch]);

  // Postcodes for the selected domain (zipcode.domain may be a nested object or id).
  const postcodes = useMemo(() => {
    const name = value.domain;
    return (zipCodesList || []).filter((zc) => {
      if (!name) return true;
      const zd = zc?.domain;
      if (zd == null) return false;
      if (typeof zd === "object")
        return zd.domain === name || String(zd.id) === String(value.domainId);
      return String(zd) === String(value.domainId);
    });
  }, [zipCodesList, value.domain, value.domainId]);

  const supportsRef = REFINEMENT_DOMAINS.includes(value.domain);

  return (
    <div className="form-stack">
      <div className="row-2">
        <div className="mca-field">
          <label className="mca-label">
            Amazon domain <span className="req">*</span>
          </label>
          <select
            className="mca-select"
            value={value.domainId || ""}
            onChange={(e) => {
              const d = amazonDomains.find((x) => String(x.id) === e.target.value);
              onChange({
                domainId: e.target.value,
                domain: d?.domain || "",
                postcode: "",
              });
            }}
          >
            <option value="" disabled>
              Select domain…
            </option>
            {amazonDomains.map((d) => (
              <option key={d.id} value={d.id}>
                {d.domain}
              </option>
            ))}
          </select>
        </div>

        <div className="mca-field">
          <label className="mca-label">
            Postcode <span className="req">*</span>
          </label>
          {value.domainId && postcodes.length === 0 ? (
            <span className="mca-help" style={{ color: "var(--mca-danger)" }}>
              No active postcodes (cookies) for {value.domain || "this domain"}.{" "}
              <Link to="/postcodes">Add one on the Postcodes/ZIP page first.</Link>
            </span>
          ) : (
            <select
              className="mca-select"
              value={value.postcode || ""}
              onChange={(e) => onChange({ postcode: e.target.value })}
            >
              <option value="" disabled>
                Select postcode…
              </option>
              {postcodes.map((z) => (
                <option key={z.id} value={z.zipcode}>
                  {z.zipcode}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="mca-field">
        <label className="mca-label">
          Category URL <span className="req">*</span>
        </label>
        <input
          className="mca-input"
          type="url"
          placeholder="https://www.amazon.com.au/s?rh=n%3A4885741051"
          value={value.category_url || ""}
          onChange={(e) => {
            const url = e.target.value;
            const patch = { category_url: url };
            if (!value.nodeTouched) {
              const ex = extractNodeId(url);
              if (ex) patch.amazon_node_id = ex;
            }
            onChange(patch);
          }}
        />
      </div>

      <div className="mca-field">
        <label className="mca-label">
          Amazon node ID <span className="req">*</span>
        </label>
        <input
          className="mca-input mca-cell-mono"
          type="text"
          maxLength={64}
          value={value.amazon_node_id || ""}
          onChange={(e) =>
            onChange({ amazon_node_id: e.target.value, nodeTouched: true })
          }
        />
        <span className="mca-help">
          Auto-filled from the category URL — edit if incorrect.
        </span>
      </div>

      <div className="row-2">
        <div className="mca-field">
          <label className="mca-label">Min price ($)</label>
          <input
            className="mca-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="0"
            value={value.price_min || ""}
            onChange={(e) => onChange({ price_min: e.target.value })}
          />
        </div>
        <div className="mca-field">
          <label className="mca-label">Max price ($)</label>
          <input
            className="mca-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="No max"
            value={value.price_max || ""}
            onChange={(e) => onChange({ price_max: e.target.value })}
          />
        </div>
      </div>

      {supportsRef && (
        <div className="row-2">
          <div className="mca-field">
            <label className="mca-label">Delivery filter</label>
            <select
              className="mca-select"
              value={value.filter_delivery_day || ""}
              onChange={(e) => onChange({ filter_delivery_day: e.target.value })}
            >
              {deliveryDayOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mca-field" style={{ justifyContent: "flex-end" }}>
            <label className="mca-check" style={{ marginBottom: "8px" }}>
              <input
                type="checkbox"
                checked={!!value.filter_prime_ships_from_au}
                onChange={(e) =>
                  onChange({ filter_prime_ships_from_au: e.target.checked })
                }
              />
              <span className="mca-check__box">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              Prime ships from AU
            </label>
          </div>
        </div>
      )}
    </div>
  );
};
